import React, { createContext, useContext, useEffect, useState } from 'react';
import { getReactToken, getSessionToken, JWTResponseDTO } from '../api/auth.api';
import { setAuthToken } from '../providers/authTokenProvider';

type AuthContextType = {
    tokens: {
        sessionToken: string;
        reactToken: string;
    };
    isAuthenticated: boolean;
    isTokenLoading: boolean;
    userInfo: JWTResponseDTO['user'] | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {
    const [ sessionToken, setSessionToken ] = useState<string>('');
    const [ reactToken, setReactToken ] = useState<string>('');
    const [ sessionTokenLoading, setSessionTokenLoading ] = useState(true);
    const [ reactTokenLoading, setReactTokenLoading ] = useState(true);
    const [ userInfo, setUserInfo ] = useState<JWTResponseDTO['user'] | null>(null);

    useEffect(() => {
        const sessionToken = localStorage.getItem('sessionToken') || '';
        const reactToken = localStorage.getItem('reactToken') || '';

        if (sessionToken) {
            setSessionToken(sessionToken);
            setSessionTokenLoading(false);
            setAuthToken('sessionToken', sessionToken);
        } else {
            getSessionToken()
            .then((response) => {
                const { token, userInfo } = response;
                setSessionToken(token);
                setUserInfo(userInfo);
                localStorage.setItem('sessionToken', token);
                setAuthToken('sessionToken', token);
            })
            .finally(() => {
                setSessionTokenLoading(false);
            });
        }

        if (reactToken) {
            setReactToken(reactToken);
            setAuthToken('reactToken', reactToken);
        } else {
            getReactToken()
            .then((response) => {
                const { token } = response;
                setSessionToken(token);
                localStorage.setItem('reactToken', token);
                setAuthToken('sessionToken', token);
            })
            .finally(() => {
                setReactTokenLoading(false);
            });
        }
    }, []);

    return (
        <AuthContext.Provider
            value={{
                tokens: { sessionToken, reactToken },
                isAuthenticated: !!sessionToken,
                isTokenLoading: sessionTokenLoading && reactTokenLoading,
                userInfo,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return ctx;
};